import type {
  AdminUser,
  Appointment,
  BookingRequest,
  BookingRequestDetail,
  BookingRequestInput,
  Catalog,
  CatalogItemInput,
  Client,
  ClientDetail,
  ClientInput,
  DashboardMetrics,
  Employee,
  Lead,
  LeadDetail,
  LeadEvent,
  Notification,
  Property,
  PropertyInput,
  QuickBooksStatus,
  SquareOverview,
  SquareCustomer,
  SquareBooking,
  ReportOverview,
  Region,
  RegionCity,
  ServiceAddon,
  ServiceType,
  ServiceTypeInput,
  SchedulingConflict,
  Team,
  TeamDetail,
  TeamMembership,
  WorkforceOverview,
  WorkingHours,
} from "./types";

const baseUrl = (import.meta.env.VITE_API_BASE_URL?.trim() ?? "").replace(/\/$/, "");

type ApiResponse<T> = { data: T };

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly conflicts: SchedulingConflict[] = [],
  ) {
    super(code);
  }
}

export async function getSession(): Promise<AdminUser | null> {
  try {
    const response = await request<ApiResponse<{ user: AdminUser }>>("/v1/auth/session");
    return response.data.user;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export async function login(email: string, password: string): Promise<AdminUser> {
  const response = await request<ApiResponse<{ user: AdminUser }>>("/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  return response.data.user;
}

export async function logout(): Promise<void> {
  await request<void>("/v1/auth/logout", { method: "POST" });
}

export async function getDashboard(): Promise<DashboardMetrics> {
  const response = await request<ApiResponse<{ metrics: DashboardMetrics }>>("/v1/admin/dashboard");
  return response.data.metrics;
}

export async function getReportOverview(from: string, to: string): Promise<ReportOverview> {
  const query = new URLSearchParams({ from, to });
  const response = await request<ApiResponse<ReportOverview>>(
    `/v1/admin/reports/overview?${query.toString()}`,
  );
  return response.data;
}

export async function getNotifications(): Promise<Notification[]> {
  const response = await request<ApiResponse<{ items: Notification[] }>>(
    "/v1/admin/notifications?limit=50",
  );
  return response.data.items;
}

export async function retryNotification(id: string): Promise<void> {
  await request(`/v1/admin/notifications/${id}/retry`, { method: "POST" });
}

export async function getLeads(
  cursor?: string,
): Promise<{ items: Lead[]; nextCursor: string | null }> {
  const query = new URLSearchParams({ limit: "20" });
  if (cursor) query.set("cursor", cursor);
  const response = await request<ApiResponse<{ items: Lead[]; nextCursor: string | null }>>(
    `/v1/admin/leads?${query}`,
  );
  return response.data;
}

export async function getLead(id: string): Promise<LeadDetail> {
  const response = await request<ApiResponse<{ lead: LeadDetail }>>(`/v1/admin/leads/${id}`);
  return response.data.lead;
}

export async function updateLeadStatus(id: string, status: string): Promise<void> {
  await request(`/v1/admin/leads/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function addLeadNote(id: string, body: string): Promise<LeadEvent> {
  const response = await request<ApiResponse<{ event: LeadEvent }>>(`/v1/admin/leads/${id}/notes`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
  return response.data.event;
}

export async function getClients(
  input: {
    cursor?: string;
    search?: string;
    limit?: number;
  } = {},
): Promise<{ items: Client[]; nextCursor: string | null }> {
  const query = new URLSearchParams({ limit: String(input.limit ?? 20) });
  if (input.cursor) query.set("cursor", input.cursor);
  if (input.search) query.set("search", input.search);
  const response = await request<ApiResponse<{ items: Client[]; nextCursor: string | null }>>(
    `/v1/admin/clients?${query}`,
  );
  return response.data;
}

export async function getClient(id: string): Promise<ClientDetail> {
  const response = await request<ApiResponse<{ client: ClientDetail }>>(`/v1/admin/clients/${id}`);
  return response.data.client;
}

export async function createClient(input: ClientInput): Promise<ClientDetail> {
  const response = await request<ApiResponse<{ client: ClientDetail }>>("/v1/admin/clients", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data.client;
}

export async function updateClient(id: string, input: Partial<ClientInput>): Promise<ClientDetail> {
  const response = await request<ApiResponse<{ client: ClientDetail }>>(`/v1/admin/clients/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return response.data.client;
}

export async function createProperty(clientId: string, input: PropertyInput): Promise<Property> {
  const response = await request<ApiResponse<{ property: Property }>>(
    `/v1/admin/clients/${clientId}/properties`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return response.data.property;
}

export async function updateProperty(
  clientId: string,
  propertyId: string,
  input: Partial<PropertyInput>,
): Promise<Property> {
  const response = await request<ApiResponse<{ property: Property }>>(
    `/v1/admin/clients/${clientId}/properties/${propertyId}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return response.data.property;
}

export async function getCatalog(): Promise<Catalog> {
  const response = await request<ApiResponse<Catalog>>("/v1/admin/catalog");
  return response.data;
}

export async function createService(input: ServiceTypeInput): Promise<ServiceType> {
  const response = await request<ApiResponse<{ service: ServiceType }>>("/v1/admin/services", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data.service;
}

export async function updateService(
  id: string,
  input: Partial<ServiceTypeInput>,
): Promise<ServiceType> {
  const response = await request<ApiResponse<{ service: ServiceType }>>(
    `/v1/admin/services/${id}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
  return response.data.service;
}

export async function createAddon(input: CatalogItemInput): Promise<ServiceAddon> {
  const response = await request<ApiResponse<{ addon: ServiceAddon }>>("/v1/admin/addons", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data.addon;
}

export async function updateAddon(
  id: string,
  input: Partial<CatalogItemInput>,
): Promise<ServiceAddon> {
  const response = await request<ApiResponse<{ addon: ServiceAddon }>>(`/v1/admin/addons/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return response.data.addon;
}

export async function getBookings(
  input: { cursor?: string; search?: string; status?: string } = {},
): Promise<{ items: BookingRequest[]; nextCursor: string | null }> {
  const query = new URLSearchParams({ limit: "25" });
  if (input.cursor) query.set("cursor", input.cursor);
  if (input.search) query.set("search", input.search);
  if (input.status) query.set("status", input.status);
  const response = await request<
    ApiResponse<{ items: BookingRequest[]; nextCursor: string | null }>
  >(`/v1/admin/booking-requests?${query}`);
  return response.data;
}

export async function getBooking(id: string): Promise<BookingRequestDetail> {
  const response = await request<ApiResponse<{ booking: BookingRequestDetail }>>(
    `/v1/admin/booking-requests/${id}`,
  );
  return response.data.booking;
}

export async function createBooking(input: BookingRequestInput): Promise<BookingRequestDetail> {
  const response = await request<ApiResponse<{ booking: BookingRequestDetail }>>(
    "/v1/admin/booking-requests",
    { method: "POST", body: JSON.stringify(input) },
  );
  return response.data.booking;
}

export async function updateBooking(
  id: string,
  input: Partial<BookingRequestInput>,
): Promise<BookingRequestDetail> {
  const response = await request<ApiResponse<{ booking: BookingRequestDetail }>>(
    `/v1/admin/booking-requests/${id}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return response.data.booking;
}

export async function convertBooking(
  id: string,
  input: {
    teamId: string;
    startAt: string;
    status: "TENTATIVE" | "CONFIRMED";
    internalNotes?: string | null;
    expectedUpdatedAt: string;
  },
): Promise<Appointment> {
  const response = await request<ApiResponse<{ appointment: Appointment }>>(
    `/v1/admin/booking-requests/${id}/convert`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return response.data.appointment;
}

export async function getQuickBooksStatus(): Promise<QuickBooksStatus> {
  const response = await request<ApiResponse<QuickBooksStatus>>(
    "/v1/integrations/quickbooks/status",
  );
  return response.data;
}

export async function beginQuickBooksConnection(): Promise<string> {
  const response = await request<ApiResponse<{ authorizationUrl: string }>>(
    "/v1/integrations/quickbooks/connect",
  );
  return response.data.authorizationUrl;
}

export async function testQuickBooksConnection(): Promise<{
  connected: boolean;
  realmId: string;
  companyName: string | null;
}> {
  const response = await request<
    ApiResponse<{ connected: boolean; realmId: string; companyName: string | null }>
  >("/v1/integrations/quickbooks/test", { method: "POST" });
  return response.data;
}

export async function disconnectQuickBooks(): Promise<boolean> {
  const response = await request<ApiResponse<{ disconnected: boolean }>>(
    "/v1/integrations/quickbooks/disconnect",
    { method: "POST" },
  );
  return response.data.disconnected;
}

export async function getSquareOverview(): Promise<SquareOverview> {
  const response = await request<ApiResponse<SquareOverview>>("/v1/integrations/square/overview");
  return response.data;
}

export async function getSquareCustomers(input: { cursor?: string; limit?: number } = {}): Promise<{
  state: SquareOverview["customers"]["state"];
  items: SquareCustomer[];
  nextCursor: string | null;
  totalCount: number | null;
  errorCode: string | null;
}> {
  const query = new URLSearchParams({ limit: String(input.limit ?? 25) });
  if (input.cursor) query.set("cursor", input.cursor);
  const response = await request<
    ApiResponse<{
      state: SquareOverview["customers"]["state"];
      items: SquareCustomer[];
      nextCursor: string | null;
      totalCount: number | null;
      errorCode: string | null;
    }>
  >(`/v1/integrations/square/customers?${query}`);
  return response.data;
}

export async function getSquareBookings(input: { cursor?: string; limit?: number } = {}): Promise<{
  state: SquareOverview["bookings"]["state"];
  items: SquareBooking[];
  nextCursor: string | null;
  errorCode: string | null;
}> {
  const query = new URLSearchParams({ limit: String(input.limit ?? 25) });
  if (input.cursor) query.set("cursor", input.cursor);
  const response = await request<
    ApiResponse<{
      state: SquareOverview["bookings"]["state"];
      items: SquareBooking[];
      nextCursor: string | null;
      errorCode: string | null;
    }>
  >(`/v1/integrations/square/bookings?${query}`);
  return response.data;
}

export async function getWorkforce(): Promise<WorkforceOverview> {
  const response = await request<ApiResponse<WorkforceOverview>>("/v1/admin/workforce");
  return response.data;
}

export async function getTeam(id: string): Promise<TeamDetail> {
  const response = await request<ApiResponse<{ team: TeamDetail }>>(`/v1/admin/teams/${id}`);
  return response.data.team;
}

export async function createRegion(input: {
  name: string;
  description?: string | null;
  active?: boolean;
}): Promise<Region> {
  const response = await request<ApiResponse<{ region: Region }>>("/v1/admin/regions", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data.region;
}

export async function updateRegion(
  id: string,
  input: { name?: string; description?: string | null; active?: boolean },
): Promise<Region> {
  const response = await request<ApiResponse<{ region: Region }>>(`/v1/admin/regions/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return response.data.region;
}

export async function createRegionCity(
  regionId: string,
  input: { city: string; state?: string; zipPattern?: string | null; active?: boolean },
): Promise<RegionCity> {
  const response = await request<ApiResponse<{ city: RegionCity }>>(
    `/v1/admin/regions/${regionId}/cities`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return response.data.city;
}

export async function createEmployee(input: {
  name: string;
  email: string;
  phone: string;
  status?: "ACTIVE" | "INACTIVE";
  capabilities?: string[];
  defaultRegionId?: string | null;
  notes?: string | null;
}): Promise<Employee> {
  const response = await request<ApiResponse<{ employee: Employee }>>("/v1/admin/employees", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data.employee;
}

export async function updateEmployee(
  id: string,
  input: Partial<{
    name: string;
    email: string;
    phone: string;
    status: "ACTIVE" | "INACTIVE";
    capabilities: string[];
    defaultRegionId: string | null;
    notes: string | null;
  }>,
): Promise<Employee> {
  const response = await request<ApiResponse<{ employee: Employee }>>(`/v1/admin/employees/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return response.data.employee;
}

export async function createTeam(input: {
  name: string;
  active?: boolean;
  defaultRegionId?: string | null;
  notes?: string | null;
}): Promise<TeamDetail> {
  const response = await request<ApiResponse<{ team: TeamDetail }>>("/v1/admin/teams", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data.team;
}

export async function updateTeam(
  id: string,
  input: Partial<{
    name: string;
    active: boolean;
    defaultRegionId: string | null;
    notes: string | null;
  }>,
): Promise<TeamDetail> {
  const response = await request<ApiResponse<{ team: TeamDetail }>>(`/v1/admin/teams/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return response.data.team;
}

export async function createMembership(
  teamId: string,
  input: { employeeId: string; validFrom: string; validUntil?: string | null },
): Promise<TeamMembership> {
  const response = await request<ApiResponse<{ membership: TeamMembership }>>(
    `/v1/admin/teams/${teamId}/memberships`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return response.data.membership;
}

export async function updateMembership(
  id: string,
  input: { validFrom?: string; validUntil?: string | null },
): Promise<TeamMembership> {
  const response = await request<ApiResponse<{ membership: TeamMembership }>>(
    `/v1/admin/team-memberships/${id}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return response.data.membership;
}

export async function createWorkingHours(
  teamId: string,
  input: {
    weekday: number;
    startTime: string;
    endTime: string;
    effectiveFrom: string;
    effectiveUntil?: string | null;
    active?: boolean;
  },
): Promise<WorkingHours> {
  const response = await request<ApiResponse<{ workingHours: WorkingHours }>>(
    `/v1/admin/teams/${teamId}/working-hours`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return response.data.workingHours;
}

export async function updateWorkingHours(
  id: string,
  input: Partial<{
    weekday: number;
    startTime: string;
    endTime: string;
    effectiveFrom: string;
    effectiveUntil: string | null;
    active: boolean;
  }>,
): Promise<WorkingHours> {
  const response = await request<ApiResponse<{ workingHours: WorkingHours }>>(
    `/v1/admin/working-hours/${id}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return response.data.workingHours;
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body != null && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      error?: { code?: string; conflicts?: SchedulingConflict[] };
    } | null;
    throw new ApiError(
      payload?.error?.code ?? "REQUEST_FAILED",
      response.status,
      payload?.error?.conflicts ?? [],
    );
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}
